/**
 * SERRULINK — <sl-box-fit>
 *
 * Garde son contenu sur UNE seule ligne, a la plus grande taille possible.
 * Part de la taille max et reduit jusqu'a ce que le texte tienne dans la largeur.
 *
 * Attributs :
 *   data-max  taille de depart en px (defaut 16)
 *   data-min  taille plancher en px  (defaut 8)
 *
 * Se recalcule : au redimensionnement, quand le contenu change, et sur
 * l'evenement serrulink:ready (la localisation peut arriver apres le rendu).
 */
class SlBoxFit extends HTMLElement {
  #resizeObserver = null;
  #mutationObserver = null;
  #onReady = null;
  #raf = null;

  connectedCallback() {
    this.#schedule();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#schedule());
      // On observe le parent : c'est lui qui impose la largeur disponible.
      this.#resizeObserver.observe(this.parentElement || this);
    } else {
      window.addEventListener('resize', this.#schedule);
    }

    // Le texte peut changer apres coup ({localisation}, data-sl, geotarget async)
    if ('MutationObserver' in window) {
      this.#mutationObserver = new MutationObserver(() => this.#schedule());
      this.#mutationObserver.observe(this, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    this.#onReady = () => this.#schedule();
    document.addEventListener('serrulink:ready', this.#onReady);
  }

  disconnectedCallback() {
    if (this.#resizeObserver) this.#resizeObserver.disconnect();
    if (this.#mutationObserver) this.#mutationObserver.disconnect();
    if (this.#onReady) document.removeEventListener('serrulink:ready', this.#onReady);
    if (this.#raf) cancelAnimationFrame(this.#raf);
  }

  /* Un seul calcul par frame, meme si plusieurs signaux arrivent ensemble */
  #schedule = () => {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => {
      this.#raf = null;
      this.#fit();
    });
  };

  #fit() {
    const max = parseFloat(this.dataset.max) || 16;
    const min = parseFloat(this.dataset.min) || 8;
    const step = 0.5;

    // Le MutationObserver ne doit pas reagir a nos propres changements de style
    if (this.#mutationObserver) this.#mutationObserver.disconnect();

    let size = max;
    this.style.fontSize = size + 'px';

    /* scrollWidth = largeur reelle du contenu, clientWidth = largeur de la boite.
       Tant que le contenu deborde, on descend d'un demi-pixel. */
    let safety = 200;
    while (this.scrollWidth > this.clientWidth && size > min && safety-- > 0) {
      size -= step;
      this.style.fontSize = size + 'px';
    }

    this.dataset.fitted = String(size);

    if (this.#mutationObserver) {
      this.#mutationObserver.observe(this, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
  }
}

if (!customElements.get('sl-box-fit')) {
  customElements.define('sl-box-fit', SlBoxFit);
}
